import { PocButton } from 'commons/components/components-wrapper/poc-button.component';
import { CustomField } from 'core/service/custom-fields/custom-field.type';
import { SettingsWorkflowOverlay } from 'modules/settings/components';
import { POCFieldAttribute } from 'modules/settings/pods/settings-integrations/service';
import React from 'react';
import {
    ActiveDirectoryDetailsFormState,
    ActiveDirectoryDetailsStep,
    ActiveDirectoryDetailsSteps,
    IdentityProviderMapping,
    TestConnectionState,
} from 'modules/settings/pods/settings-integrations/settings-integrations-active-directory/types/active-directory.type';
import { ActiveDirectoryDetailsItem } from './active-directory-details-item.vm';
import { ActiveDirectoryConnectionDetailsForm, ActiveDirectoryDataPointsForm } from './components';
import { ConnectionFormFieldErrors } from './components/active-directory-connection-details-form/active-directory-connection-details-form.business';
import { ActiveDirectoryFormWrapper, ScrollableContent } from './settings-integrations-active-directory-details.style';

interface SettingsIntegrationsActiveDirectoryDetailsComponentProps {
    activeDirectory?: ActiveDirectoryDetailsItem;
    formState: ActiveDirectoryDetailsFormState;
    testConnectionState: TestConnectionState;
    identityProviderAttributes: string[];
    operatorsCustomFields: CustomField[];
    pocFields: POCFieldAttribute[];
    identityProviderMapping: IdentityProviderMapping[];
    currentStep: ActiveDirectoryDetailsStep;
    isDirty: boolean;
    isEditMode: boolean;
    loading: boolean;
    headerTitle: string;
    footerPrimaryButtonText: string;
    footerSecondaryButtonText: string;
    isFooterPrimaryButtonDisabled: boolean;
    isFooterSecondaryButtonDisabled: boolean;
    onFooterPrimaryButtonClick: VoidFunction;
    onFooterSecondaryButtonClick: VoidFunction;
    onChangeField: (field: string, value: string | IdentityProviderMapping[]) => void;
    fieldErrors?: ConnectionFormFieldErrors;
    onTestConnection: () => Promise<void>;
    onEditConnectionDetails: VoidFunction;
    onClose: VoidFunction;
}

export const SettingsIntegrationsActiveDirectoryDetailsComponent: React.FC<
    SettingsIntegrationsActiveDirectoryDetailsComponentProps
> = ({
    activeDirectory,
    formState,
    testConnectionState,
    identityProviderAttributes,
    operatorsCustomFields,
    identityProviderMapping,
    pocFields,
    currentStep,
    isDirty,
    isEditMode,
    loading,
    headerTitle,
    footerPrimaryButtonText,
    footerSecondaryButtonText,
    isFooterPrimaryButtonDisabled,
    isFooterSecondaryButtonDisabled,
    onFooterPrimaryButtonClick,
    onFooterSecondaryButtonClick,
    onChangeField,
    fieldErrors,
    onTestConnection,
    onEditConnectionDetails,
    onClose,
}) => (
    <SettingsWorkflowOverlay
        visible
        loading={loading}
        headerTitle={headerTitle}
        onClose={onClose}
        className="show-header"
        footer={[
            <React.Fragment key="footer-actions">
                <PocButton
                    key={'cancel-button'}
                    data-testid="active-directory-details-cancel-button"
                    variant="secondary"
                    onClick={onFooterSecondaryButtonClick}
                    disabled={isFooterSecondaryButtonDisabled}
                >
                    {footerSecondaryButtonText}
                </PocButton>
                <PocButton
                    key={'save-button'}
                    onClick={onFooterPrimaryButtonClick}
                    disabled={isFooterPrimaryButtonDisabled}
                >
                    {footerPrimaryButtonText}
                </PocButton>
            </React.Fragment>,
        ]}
    >
        <ScrollableContent>
            <ActiveDirectoryFormWrapper>
                {currentStep === ActiveDirectoryDetailsSteps.CONNECTION && (
                    <ActiveDirectoryConnectionDetailsForm
                        isDirty={isDirty}
                        isEditMode={isEditMode}
                        activeDirectory={activeDirectory}
                        formState={formState}
                        testConnectionState={testConnectionState}
                        fieldErrors={fieldErrors}
                        onChangeField={onChangeField}
                        onTestConnection={onTestConnection}
                        onEditConnectionDetails={onEditConnectionDetails}
                    />
                )}
                {currentStep === ActiveDirectoryDetailsSteps.DATA_POINTS && (
                    <ActiveDirectoryDataPointsForm
                        pocFields={pocFields}
                        customFields={operatorsCustomFields}
                        identityProviderAttributes={identityProviderAttributes}
                        identityProviderMapping={identityProviderMapping}
                        onChangeField={onChangeField}
                    />
                )}
            </ActiveDirectoryFormWrapper>
        </ScrollableContent>
    </SettingsWorkflowOverlay>
);
